import { useEffect, useMemo, useRef, useState } from 'react';
import type { JSX } from 'react';
import type { TakiConfig } from '@taki/runtime-config';
import {
  ACCENT_TAKI,
  Button,
  ConfirmDialog,
  SettingsActions,
  SettingsColor,
  SettingsFile,
  SettingsGroup,
  SettingsInput,
  SettingsMessage,
  SettingsPanel,
  SettingsRow,
  SettingsSave,
  SettingsSection,
  TakiIcon,
  normaliserHex,
  repliDepuisConfig,
  useBrandingPort,
} from '@taki/ui';
import type { BrandingEffectif, BrandingPatch, BrandingPersonnalise } from '@taki/ui';
import { HOME_PANEL_SCALE, LogoAccueil } from '../logoAccueil.js';
import { messageDErreur } from './MembresSettingsPanel.js';
import { FuseauSettingsSection } from './FuseauSettingsSection.js';
import {
  ACCEPT_LOGO,
  GABARIT_ICONE,
  GABARIT_LOGO,
  dataUrlDeFichierImage,
} from './logoClient.js';

/**
 * L'ONGLET « ENVIRONNEMENT » DU HUB PARAMETRES.
 *
 * CE QU'IL FAIT : donner au client le nom, le logo, l'icone et la couleur
 * d'accent de SON environnement, par-dessus ce que la configuration de
 * deploiement dit ; et son fuseau horaire (`FuseauSettingsSection`, owner seul).
 *
 * CE QU'IL NE FAIT PAS : de la securite. Les Firestore Security Rules refusent
 * deja l'ecriture a qui n'est pas owner ou admin, et bornent le nom, le poids
 * des deux images et la forme de l'accent. Ce panneau se FERME pour les autres, avec la
 * raison ecrite (G16 : on explique, on ne cache pas), et affiche le refus quand
 * il arrive quand meme. Il ne remplace jamais les rules.
 *
 * IL NE PARLE PAS AU SDK : le shell n'a pas `@taki/sdk` dans ses dependances,
 * et c'est voulu. Tout passe par le PORT (`BrandingPort`, @taki/ui) que l'hote
 * remplit (`apps/hello/src/branding.ts`).
 *
 * LE BROUILLON SE VOIT AVANT D'ETRE ENREGISTRE, et c'est le sujet de l'ecran :
 * chaque frappe est poussee dans `port.previsualiser(...)`, donc TOUTE
 * l'interface se repeint, la barre de Parametres comprise. Quitter l'onglet ou
 * annuler retire l'apercu : on ne laisse jamais un client avec une couleur
 * qu'il n'a pas enregistree.
 */

/** La borne des rules sur le nom : au-dela, l'ecriture est refusee au serveur. */
export const NOM_MAX = 80;

/**
 * La raison du panneau ferme, dans le registre de `RAISONS_ACCES` (@taki/ui) :
 * une phrase courte qui dit QUI peut, jamais un « acces refuse ». Celle des
 * utilisateurs ne convient pas (elle parle des utilisateurs) ; celle-ci lui
 * repond mot pour mot, sur le meme droit (owner ou admin actif).
 */
const RAISON_RESERVEE =
  "Réservé aux propriétaires et administrateurs.";

/*
 * LES PASTILLES PROPOSEES, ET LA SEULE EXCEPTION A « PAS DE HEX EN DUR ».
 *
 * Ce ne sont pas des tokens, ce sont des COULEURS OFFERTES AU CHOIX, comme le
 * contenu d'un nuancier. Un token dit « la couleur d'accent » et change avec
 * l'environnement ; ces sept valeurs, elles, ne changent jamais, et c'est
 * exactement ce qu'on leur demande. Il n'existe pas, et il ne peut pas exister,
 * de token « le bleu propose ».
 *
 * Le lime Taki en premier (c'est le defaut du socle), puis six couleurs sobres,
 * assez foncees pour que l'encre calculee par-dessus reste lisible.
 */
const PASTILLES: ReadonlyArray<{ hex: string; nom: string }> = [
  { hex: ACCENT_TAKI, nom: 'Lime Taki' },
  { hex: '#2f6fed', nom: 'Bleu' },
  { hex: '#1f9d55', nom: 'Vert' },
  { hex: '#e8710a', nom: 'Orange' },
  { hex: '#d6336c', nom: 'Rose' },
  { hex: '#7048e8', nom: 'Violet' },
  { hex: '#3f4451', nom: 'Anthracite' },
];

/* -------------------------------------------------------------------------- */
/* Les fonctions PURES de l'ecran : ce qui part dans le document.              */
/* Elles n'appellent rien de @taki/ui, et c'est ce qui les rend prouvables en  */
/* `node --test` (tests/environnement.test.mjs) : la doublure de @taki/ui ne   */
/* fournit que des noms, jamais des valeurs appelables.                        */
/* -------------------------------------------------------------------------- */

/** Ce que l'ecran tient en cours d'edition, une fois la saisie hex jugee. */
export interface BrouillonBranding {
  /** Le nom saisi, tel quel (il sera coupe et debarrasse de ses espaces). */
  nom: string;
  /** La data URL choisie, ou `null` : garder le logo de la configuration. */
  logo: string | null;
  /** L'icone choisie, ou `null` : garder celle de la configuration (a defaut, le chevron Taki). */
  icon: string | null;
  /** L'accent DEJA normalise (`normaliserHex`), ou `null` si ce n'est pas un hex. */
  accent: string | null;
}

/**
 * CE QUI PART DANS LE DOCUMENT, et la regle tient en une phrase : un champ egal
 * au repli s'enregistre a `null`.
 *
 * POURQUOI : personnaliser un champ, c'est le FIGER. Enregistrer « Taki » parce
 * que la configuration dit « Taki » gele ce nom dans le document ; le jour ou
 * le deploiement change le nom, l'environnement garderait l'ancien, et personne
 * ne saurait pourquoi. Meme piege avec le lime : enregistrer `#cbfb45` sur un
 * environnement deja lime survivrait a un changement de charte.
 */
export function patchDeBranding(
  brouillon: BrouillonBranding,
  repli: BrandingEffectif,
): BrandingPatch {
  const nom = brouillon.nom.trim().slice(0, NOM_MAX);
  return {
    name: nom === '' || nom === repli.name ? null : nom,
    logo: brouillon.logo === null || brouillon.logo === repli.logo ? null : brouillon.logo,
    icon: brouillon.icon === null || brouillon.icon === repli.icon ? null : brouillon.icon,
    accent:
      brouillon.accent === null || brouillon.accent === repli.accent ? null : brouillon.accent,
  };
}

/**
 * CE QUE L'APERCU MONTRE PENDANT LA FRAPPE, et la regle tient en une phrase :
 * une saisie hex incomplete garde la DERNIERE couleur valide.
 *
 * POURQUOI : taper une couleur a la main passe par « # », « #3 », « #3f »...
 * Chacun de ces etats n'est pas un hex, donc le patch porterait `accent: null`,
 * c'est-a-dire « pas personnalise », c'est-a-dire le lime de la configuration.
 * L'interface entiere clignoterait entre la couleur choisie et celle de Taki a
 * chaque touche. Ce qui est refuse, c'est l'ENREGISTREMENT (`peutEnregistrer`),
 * pas l'affichage.
 */
export function patchDApercu(
  brouillon: BrouillonBranding,
  dernierAccentValide: string,
  repli: BrandingEffectif,
): BrandingPatch {
  return patchDeBranding({ ...brouillon, accent: brouillon.accent ?? dernierAccentValide }, repli);
}

/** Une valeur absente, vide ou `null` sont le meme fait : « pas personnalise ». */
function efface(valeur: string | null | undefined): string | null {
  return valeur === undefined || valeur === null || valeur === '' ? null : valeur;
}

/** Le patch calcule dit-il deja exactement ce qui est enregistre ? */
export function memePersonnalisation(
  patch: BrandingPatch,
  enregistre: BrandingPersonnalise,
): boolean {
  return (
    efface(patch.name) === efface(enregistre.name) &&
    efface(patch.logo) === efface(enregistre.logo) &&
    efface(patch.icon) === efface(enregistre.icon) &&
    efface(patch.accent) === efface(enregistre.accent)
  );
}

/**
 * Y a-t-il seulement quelque chose a reinitialiser ? « Tout reinitialiser »
 * efface le document ; sur un environnement qui n'a jamais rien choisi, le
 * bouton promettrait un geste sans effet, et la fenetre de confirmation le
 * ferait croire important.
 */
export function estPersonnalise(enregistre: BrandingPersonnalise): boolean {
  return (
    efface(enregistre.name) !== null ||
    efface(enregistre.logo) !== null ||
    efface(enregistre.icon) !== null ||
    efface(enregistre.accent) !== null
  );
}

/**
 * Le bouton « Enregistrer » est-il actif ? Deux refus, et un seul endroit pour
 * les deux : une saisie hex qui n'en est pas une (le brouillon porte alors
 * `null`), et un brouillon qui dit deja ce qui est enregistre.
 */
export function peutEnregistrer(
  brouillon: BrouillonBranding,
  repli: BrandingEffectif,
  enregistre: BrandingPersonnalise,
): boolean {
  if (brouillon.accent === null) return false;
  return !memePersonnalisation(patchDeBranding(brouillon, repli), enregistre);
}

/* -------------------------------------------------------------------------- */
/* Le panneau                                                                  */
/* -------------------------------------------------------------------------- */

/** Le brouillon de l'ecran : le hex y reste du TEXTE, il peut etre incomplet. */
interface Brouillon {
  nom: string;
  logo: string | null;
  icon: string | null;
  /** Le hex tel qu'il est tape : `#2f6` est un etat legitime de la frappe. */
  accentTexte: string;
}

/** Le brouillon d'ouverture : ce qui est enregistre, a defaut ce que la config dit. */
function brouillonDe(personnalise: BrandingPersonnalise, repli: BrandingEffectif): Brouillon {
  return {
    nom: personnalise.name ?? repli.name,
    logo: personnalise.logo ?? null,
    icon: personnalise.icon ?? null,
    accentTexte: personnalise.accent ?? repli.accent,
  };
}

/** Le brouillon tel que les fonctions pures le lisent : la saisie hex jugee. */
function juge(brouillon: Brouillon): BrouillonBranding {
  return {
    nom: brouillon.nom,
    logo: brouillon.logo,
    icon: brouillon.icon,
    accent: normaliserHex(brouillon.accentTexte),
  };
}

export interface EnvironnementSettingsPanelProps {
  /** Config runtime : le repli quand aucun port de branding n'est branche. */
  config: TakiConfig;
}

export function EnvironnementSettingsPanel({
  config,
}: EnvironnementSettingsPanelProps): JSX.Element {
  const port = useBrandingPort();
  const peutModifier = port?.peutModifier === true;

  /* LES TROIS LECTURES DU PORT, MEMOISEES, et ce n'est pas du zele : sans port,
     `?? {}` fabriquerait un objet neuf a chaque rendu, et l'effet de reprise du
     brouillon plus bas tournerait sans fin. */
  const personnalise = useMemo<BrandingPersonnalise>(() => port?.personnalise ?? {}, [port]);
  const repli = useMemo<BrandingEffectif>(
    () => port?.repli ?? repliDepuisConfig(config.branding),
    [port, config.branding],
  );
  const courant = port?.courant ?? repli;

  const [brouillon, setBrouillon] = useState<Brouillon>(() => brouillonDe(personnalise, repli));
  /** Vrai des la premiere frappe : l'ecran cesse alors de suivre le document. */
  const [modifie, setModifie] = useState(false);
  const [erreur, setErreur] = useState<string | null>(null);
  const [enregistre, setEnregistre] = useState(false);
  /** Une ecriture part : le bouton tourne, « Enregistrement… » (`SettingsSave`). */
  const [occupe, setOccupe] = useState(false);
  const [reinitOuverte, setReinitOuverte] = useState(false);

  /* LE DERNIER ACCENT QUI ETAIT UN HEX. Taper une couleur a la main passe par
     des etats incomplets (« #3f », « #3f44 ») : sans cette memoire, l'apercu
     retomberait sur le lime de la configuration a chaque frappe, et toute
     l'interface clignoterait entre la couleur choisie et celle de Taki. */
  const dernierAccentValide = useRef<string>(
    normaliserHex(personnalise.accent ?? repli.accent) ?? repli.accent,
  );

  /* LE PORT ET LE BROUILLON EN REF, lus par les gestes ASYNCHRONES (le fichier
     choisi arrive une lecture plus tard) et par le nettoyage de demontage. En
     dependance d'effet, le port relancerait l'effet a chaque apercu pousse :
     c'est lui qui change d'identite quand l'apercu change. */
  const portRef = useRef(port);
  const brouillonRef = useRef(brouillon);
  useEffect(() => {
    portRef.current = port;
  }, [port]);
  useEffect(() => {
    brouillonRef.current = brouillon;
  }, [brouillon]);

  /* L'ECRAN SUIT LE DOCUMENT TANT QU'ON N'A RIEN TOUCHE. Le premier etat vient
     du cache local (l'hote le lit avant le premier snapshot) : sans cette
     reprise, une valeur enregistree depuis un autre poste n'apparaitrait qu'au
     rechargement. Des que `modifie` passe a vrai, la reprise s'arrete : une
     frappe ne doit jamais etre ecrasee par un snapshot. « Annuler » remet
     `modifie` a faux, donc rejoue cette reprise : c'est la meme ligne. */
  useEffect(() => {
    if (modifie) return;
    const repris = brouillonDe(personnalise, repli);
    dernierAccentValide.current = normaliserHex(repris.accentTexte) ?? repli.accent;
    setBrouillon(repris);
  }, [personnalise, repli, modifie]);

  /* QUITTER L'ONGLET RETIRE L'APERCU. Sans ce nettoyage, un accent essaye puis
     abandonne suivrait l'utilisateur dans toute l'interface jusqu'au prochain
     rechargement, sans un bouton pour le retirer. */
  useEffect(
    () => () => {
      portRef.current?.previsualiser(null);
    },
    [],
  );

  /* ------------------------------------------------------------------ */
  /* Les deux panneaux fermes : on DIT pourquoi, on ne cache pas l'onglet. */
  /* ------------------------------------------------------------------ */
  if (port === null || !peutModifier) {
    return (
      <SettingsPanel>
        <SettingsSection
          title="Identité"
          contextLabel="Organisation"
          lock={port === null ? 'Personnalisation indisponible' : RAISON_RESERVEE}
        >
          <SettingsGroup title="Identité visuelle">
            <SettingsRow label="Nom">
              <span className="text-[0.8125rem] text-ink-700">{courant.name}</span>
            </SettingsRow>
            <SettingsRow label="Logo">
              <SettingsFile label="du logo" accept={ACCEPT_LOGO} personnalise={false} disabled
                onFile={() => undefined} onDefault={() => undefined}>
                <ApercuIdentite nom={courant.name} logo={courant.logo} />
              </SettingsFile>
            </SettingsRow>
            <SettingsRow label="Icône">
              <SettingsFile label="de l’icône" accept={ACCEPT_LOGO} personnalise={false} disabled
                onFile={() => undefined} onDefault={() => undefined}>
                <ApercuIcone icone={courant.icon} grand />
              </SettingsFile>
            </SettingsRow>
          </SettingsGroup>
          <SettingsGroup title="Couleurs">
            <SettingsRow label="Couleur d’accent">
              <span className="flex items-center gap-2">
                <Pastille hex={courant.accent} />
                <span className="font-mono text-[0.8125rem] text-ink-700">{courant.accent}</span>
              </span>
            </SettingsRow>
          </SettingsGroup>
        </SettingsSection>
        <FuseauSettingsSection />
      </SettingsPanel>
    );
  }

  /* ------------------------------------------------------------------ */
  /* Le panneau ouvert                                                   */
  /* ------------------------------------------------------------------ */

  const pur = juge(brouillon);
  const logoAffiche = brouillon.logo ?? repli.logo;
  const iconeAffichee = brouillon.icon ?? repli.icon;
  const actif = peutEnregistrer(pur, repli, personnalise);

  /** Chaque frappe change le brouillon ET repeint l'interface entiere. */
  function majBrouillon(patch: Partial<Brouillon>): void {
    const suivant = { ...brouillonRef.current, ...patch };
    brouillonRef.current = suivant;
    setModifie(true);
    setEnregistre(false);
    setBrouillon(suivant);
    /* L'APERCU GARDE LA DERNIERE COULEUR VALIDE tant que la saisie n'en est pas
       une : c'est ce que montre deja la pastille de l'ecran. « Enregistrer »,
       lui, reste refuse (`peutEnregistrer`) : on ne repeint pas, on n'ecrit pas
       non plus. */
    const purSuivant = juge(suivant);
    if (purSuivant.accent !== null) dernierAccentValide.current = purSuivant.accent;
    portRef.current?.previsualiser(patchDApercu(purSuivant, dernierAccentValide.current, repli));
  }

  /**
   * Un fichier choisi, pour le LOGO ou pour l'ICONE : meme mecanique, seuls le
   * gabarit (taille visee, borne) et la clef du brouillon changent.
   */
  function choisirFichier(fichier: File, quoi: 'logo' | 'icon'): void {
    setErreur(null);
    void dataUrlDeFichierImage(fichier, quoi === 'logo' ? GABARIT_LOGO : GABARIT_ICONE).then(
      (dataUrl) => majBrouillon({ [quoi]: dataUrl }),
      (cause: unknown) => setErreur(messageDErreur(cause)),
    );
  }

  function annuler(): void {
    setModifie(false);
    setEnregistre(false);
    setErreur(null);
    portRef.current?.previsualiser(null);
  }

  function enregistrer(): void {
    const actuel = portRef.current;
    if (actuel === null || occupe) return;
    setErreur(null);
    setEnregistre(false);
    setOccupe(true);
    void actuel.enregistrer(patchDeBranding(juge(brouillonRef.current), repli)).then(
      () => {
        setModifie(false);
        setEnregistre(true);
      },
      (cause: unknown) => setErreur(messageDErreur(cause)),
    ).finally(() => setOccupe(false));
  }

  function reinitialiser(): void {
    const actuel = portRef.current;
    setReinitOuverte(false);
    if (actuel === null || occupe) return;
    setErreur(null);
    setEnregistre(false);
    setOccupe(true);
    void actuel.reinitialiser().then(
      () => {
        setModifie(false);
        setEnregistre(true);
      },
      (cause: unknown) => setErreur(messageDErreur(cause)),
    ).finally(() => setOccupe(false));
  }

  return (
    <SettingsPanel>
      <SettingsSection title="Identité" contextLabel="Organisation">
        <SettingsGroup title="Identité visuelle">
          <SettingsRow label="Nom">
            <div className="w-full min-w-0">
              <SettingsInput value={brouillon.nom} maxLength={NOM_MAX}
                onChange={(event) => majBrouillon({ nom: event.target.value })}
                aria-label="Nom de l'environnement" title={`${NOM_MAX} caractères maximum`} />
              {brouillon.nom.trim() !== repli.name && (
                <div className="mt-1 flex justify-end">
                  <Button variant="secondary" size="sm" className="h-8" onClick={() => majBrouillon({ nom: repli.name })}>
                    Reprendre « {repli.name} »
                  </Button>
                </div>
              )}
            </div>
          </SettingsRow>
          <SettingsRow label="Logo">
            <SettingsFile label="du logo" accept={ACCEPT_LOGO} formats="PNG, JPEG, WebP ou SVG"
              personnalise={brouillon.logo !== null}
              onFile={(fichier) => choisirFichier(fichier, 'logo')}
              onDefault={() => majBrouillon({ logo: null })}>
              <ApercuIdentite nom={brouillon.nom.trim() === '' ? repli.name : brouillon.nom} logo={logoAffiche} />
            </SettingsFile>
          </SettingsRow>
          <SettingsRow label="Icône">
            <SettingsFile label="de l’icône" accept={ACCEPT_LOGO} formats="PNG, JPEG, WebP ou SVG"
              personnalise={brouillon.icon !== null}
              onFile={(fichier) => choisirFichier(fichier, 'icon')}
              onDefault={() => majBrouillon({ icon: null })}>
              <ApercuIcone icone={iconeAffichee} grand />
            </SettingsFile>
          </SettingsRow>
        </SettingsGroup>
        <SettingsGroup title="Couleurs">
          <SettingsRow label="Couleur d’accent">
            <SettingsColor label="Couleur d’accent" value={brouillon.accentTexte} palette={PASTILLES}
              defaut={ACCENT_TAKI} onChange={(valeur) => majBrouillon({ accentTexte: valeur })} />
          </SettingsRow>
        </SettingsGroup>
        {erreur != null && <SettingsMessage tone="error" end>{erreur}</SettingsMessage>}
        <SettingsActions>
          <SettingsSave busy={occupe} done={enregistre} disabled={!actif} onSave={enregistrer} />
          <Button variant="ghost" size="sm" disabled={!modifie || occupe} onClick={annuler}>Annuler</Button>
          <Button variant="secondary" size="sm" disabled={!estPersonnalise(personnalise) || occupe}
            onClick={() => setReinitOuverte(true)}>Tout réinitialiser</Button>
        </SettingsActions>
      </SettingsSection>
      <FuseauSettingsSection />

      <ConfirmDialog
        open={reinitOuverte}
        tone="danger"
        title="Tout réinitialiser ?"
        body={['Nom, logo, icône et couleur reviennent à l’origine.']}
        confirmLabel="Réinitialiser"
        onConfirm={reinitialiser}
        onCancel={() => setReinitOuverte(false)}
      />
    </SettingsPanel>
  );
}

/**
 * Le logo tel qu'il s'affichera, sur le fond de carte de l'accueil et A LA
 * TAILLE OU L'ACCUEIL LE MONTRE : un logo se juge a sa taille reelle, pas a une
 * vignette. D'ou le MEME composant que l'accueil ({@link LogoAccueil}), sous le
 * MEME zoom, dans une boite de la largeur utile de son rail (19.5rem moins les
 * marges, 15rem). Recopier ses classes sans le zoom le montrait 19 % trop grand.
 */
function ApercuIdentite({ nom, logo }: { nom: string; logo: string | null }): JSX.Element {
  return (
    <div className="flex min-w-0 items-center">
      <div className="w-[15rem] max-w-full" style={{ zoom: HOME_PANEL_SCALE }}>
        <LogoAccueil nom={nom} logo={logo} />
      </div>
    </div>
  );
}

/**
 * L'ICONE TELLE QU'ELLE SERA VUE, et a sa VRAIE TAILLE : 20 px, celle du haut
 * du dock. Une vignette de 64 px mentirait, et c'est exactement le piege de
 * cette image-la : tout logo est joli en grand, seul un dessin simple survit a
 * vingt pixels. `grand` ajoute la version 32 px de l'onglet du navigateur a
 * cote, sur le meme fond, pour juger les deux d'un coup.
 */
function ApercuIcone({ icone, grand = false }: { icone: string | null; grand?: boolean }): JSX.Element {
  const marque = (taille: string) =>
    icone === null ? (
      <TakiIcon width={taille} height={taille} className="text-ink-900" />
    ) : (
      <img src={icone} alt="" aria-hidden className="object-contain" style={{ width: taille, height: taille }} />
    );
  if (!grand) {
    return <span className="flex h-5 w-5 items-center justify-center">{marque('1.25rem')}</span>;
  }
  return (
    <div className="flex flex-wrap items-center gap-3">
      <span className="flex items-center gap-2 text-xs text-ink-500">{marque('1.25rem')}Dock</span>
      <span className="flex items-center gap-2 text-xs text-ink-500">{marque('2rem')}Onglet</span>
    </div>
  );
}

/** Le rond de couleur d'une pastille, la seule chose qui porte un hex en style. */
function Pastille({ hex }: { hex: string }): JSX.Element {
  return (
    <span
      aria-hidden
      className="h-4 w-4 rounded-full border border-border-soft"
      style={{ backgroundColor: hex }}
    />
  );
}

export default EnvironnementSettingsPanel;
